import React, { useEffect, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import styled from 'styled-components';
import buildQueryString from '@utils/build-query-string';
import searchBreadcrumbsFormatter from '@utils/formatters/search-breadcrumbs';
import { formatSearchCanonicalUrlAbsolute } from '@utils/formatters/search-canonical-url';
import {
  formatLocation,
  formatNamedLocation,
  formatPostcode,
} from '@utils/format-location';
import jonify from '@utils/jonify';
import { track } from 'lib/sdks/analytics';
import type { SingleCategory, Trade } from 'lib/sdks/search';
import formatPageTitle, { getMetaTitle } from '@utils/formatters/page-title';
import Schema from './Schema';
import SearchDock from './SearchDock/SearchDock';
import SearchBreadcrumbsRedesign from './search-breadcrumbs';
import getTradeTrackingData from './get-trade-tracking-data';
import { DockWrapper, SearchRedesignBottomStuff } from './styles';
import TradeSignupBannerRedesign from './trade-sign-up-banner';
import { isPostcode } from '@utils/postcode-validation';
import { useRouter } from 'next/router';

import { JobContextProvider } from 'components/Funnelling/job.context';
import { JOB_TYPE } from 'lib/sdks/jobs-management/job.constants';
import { TradeProvider } from 'components/Funnelling/trade.context';
import { useSessionUserId } from '../../../lib/auth/auth.hooks';
import { logBrazeEvent } from '../../../lib/sdks/braze/braze';
import { LocalStorageKeys } from 'components/@pages/HomePage/SearchModal/consts';
import { getAlgorithm, ALGORITHM_STORAGE_KEY } from '@utils/search-algorithm';
import { useOptimizely, Experiment } from 'lib/sdks/optimizely';
import { getTradesForCurrentPage } from './get-trades-for-current-page';
import { getHeading, replacePlaceholders } from '@utils/formatters/seo-content';
import SearchContent from './SearchContent';
import type { SearchPageProps } from './types';
import { JobRefinementProvider } from 'components/JobRefinement';
import { AppPromotionPopup } from './app-promotion-popup';
import { colors } from '@checkatrade/web-ui/themes';
import Interlinks from './Interlinks/Interlinks';

const formatLocationInput = (string: string) => {
  if (!string) {
    return;
  }

  return isPostcode(string)
    ? formatPostcode(string)
    : formatNamedLocation(string);
};

const CategoryBlurbRedesign = dynamic(() => import('./category-blurb'));

export const Wrapper = styled.div<StyledDiv>`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-color: ${colors.neutral.extraSoft};
`;

const SearchInit = dynamic(() => import('./SearchInit'));
const SearchNoResults = dynamic(() => import('./SearchNoResults'));
const SearchError = dynamic(() => import('./SearchError'));
const RefineCategoriesModal = dynamic(
  () => import('./RefineCategoriesModal/refine-categories-modal'),
);
const RAQFunnel = dynamic(() => import('components/Funnelling/RAQFunnel'), {
  ssr: true,
});

export const SearchPage = (props: SearchPageProps) => {
  const {
    categories,
    county,
    data,
    error,
    handleCategorySearch,
    loading,
    location,
    locations,
    onTrackEvent,
    postcode,
    refineCategories,
    searchKey,
    funnelJourney,
    seoContent,
    form,
  } = props;

  const localBusiness = props.data?.meta?.seoData?.localBusiness;

  if (localBusiness) {
    localBusiness.name = localBusiness.name.replaceAll('%LOCATION%', location);
    localBusiness.url = localBusiness.url.replaceAll('%LOCATION%', location);
  }

  const category = useMemo(
    () =>
      props.category || {
        label: '',
        name: '',
      },
    [props.category],
  );

  const defaultMeta = {
    itemsPerPage: undefined,
    currentPage: undefined,
    totalPages: undefined,
    totalResults: undefined,
    experiments: {
      algorithm: [],
    },
  };

  const {
    items = [],
    meta: {
      itemsPerPage,
      currentPage,
      totalPages,
      totalResults,
      experiments,
    } = defaultMeta,
  } = data || {
    items: [],
    meta: defaultMeta,
    view: {},
  };

  const [messageTradeData, setMessageTradeData] = useState<Trade | null>(null);
  const [moreTradesLoading, setMoreTradesLoading] = useState(false);
  const [showRefineCategoriesModal, setShowRefineCategoriesModal] =
    useState<boolean>(false);
  const router = useRouter();
  const userId = useSessionUserId();

  const [algorithm, setAlgorithm] = useState(
    props.algorithm || props.data?.meta?.experiments?.algorithm?.[0],
  );
  const { decide, loading: optimizelyLoading } = useOptimizely();

  const jobRefinementEnabled = useMemo(() => {
    if (optimizelyLoading) return false;
    const decision = decide(Experiment.LEAD_ENRICHMENT_FORM);
    return (
      decision?.enabled &&
      ['variant_a', 'variant_b', 'variant_c'].includes(
        decision?.variationKey ?? '',
      )
    );
  }, [optimizelyLoading, decide]);

  const noError = typeof error === 'undefined';
  const showError = !noError && !loading;
  const showInit = noError && !loading && !data;
  const showNoResults = noError && !loading && data && totalResults === 0;
  const showContent = !showInit && !showNoResults && !showError && !!data; // && !loading;
  const showAmbiguousLocations = locations && locations.length > 1 && !county;

  const formattedLocation = formatLocation(location);
  const capitalisedLocation = formatLocationInput(location);

  const noindex = seoContent?.index !== undefined ? !seoContent.index : false;
  const nofollow =
    seoContent?.follow !== undefined ? !seoContent.follow : false;

  const heading = getHeading({
    seoContent,
    categoryLabel: category.label,
    location,
    totalResults,
  });

  const pageTitle = getMetaTitle({
    metaTitle: seoContent?.pageTitle,
    location,
    categoryLabel: category.label,
    totalResults,
  });

  const blurbContent = {
    blurbHeading: seoContent?.blurbHeading,
    blurb: seoContent?.blurb,
    faqs: seoContent?.faqs,
    location: formattedLocation,
  };

  const description = seoContent?.metaDescription
    ? replacePlaceholders({
        tokenisedString: seoContent.metaDescription,
        categoryLabel: category.label,
        location: formattedLocation,
        totalResults,
      })
    : `Find trusted ${category.label} for free in ${formattedLocation} – read genuine reviews from 4 million customers. ${totalResults} local checked and vetted ${formattedLocation} ${category.label} to choose from.`;

  const breadcrumbs = searchBreadcrumbsFormatter({
    category,
    location,
  });

  const canonicalUrl = formatSearchCanonicalUrlAbsolute(
    category.name,
    location,
  );

  useEffect(() => {
    if (!optimizelyLoading) {
      setAlgorithm(getAlgorithm({ algorithm }, decide));
    }
  }, [optimizelyLoading, decide]);

  useEffect(() => {
    if (!loading) {
      trackShow();
    }

    setMoreTradesLoading(false); // Hack. If number of items changes, "More Trades" is no longer loading
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, searchKey, items]);

  useEffect(() => {
    if (category?.name && postcode) {
      logBrazeEvent('ViewedSearchResults', {
        userId,
        categoryName: category.name,
        postcode,
      });
    }
  }, [userId, category?.name, postcode]);

  const trackShow = () => {
    const status = error ? error.status || 'unknown' : '200';
    const requestId = data?.meta?.requestId || 0;
    const tradesForCurrentPage = getTradesForCurrentPage({
      items,
      currentPage,
      itemsPerPage,
    });

    onTrackEvent({
      key: 'search.show',
      data: {
        traders: tradesForCurrentPage.map((trade, index) => ({
          ...getTradeTrackingData(trade, false),
          order: index,
        })),
        category: (category || {}).id,
        location,
        postcode,
        page_number: currentPage,
        page_size: tradesForCurrentPage.length,
        total_pages: totalPages,
        total_results: totalResults,
        request_id: requestId,
        status,
        search_method: sessionStorage.getItem(LocalStorageKeys.SEARCH_METHOD),
        algorithm: experiments?.algorithm,
        algorithm_context: {
          api: experiments?.algorithm,
          passedIn: props.algorithm,
          sessionStorage: window?.sessionStorage.getItem(ALGORITHM_STORAGE_KEY),
          localVariable: algorithm,
        },
      },
    });
  };

  const buildUrls = (newPage: number, options: {}) => {
    return {
      href: `/Search?${buildQueryString({
        page: newPage,
        categoryId: category.id,
        location,
        county,
        ...options,
      })}`,
    };
  };

  const buildVanityUrl = React.useCallback(
    ({
      vanityCategory,
      location: newLocation,
    }: {
      vanityCategory?: string;
      location?: string;
    }) => {
      const urlLocation = newLocation ? jonify(newLocation) : location;
      const urlCategory = vanityCategory ?? jonify(category.name);

      const href = `/Search/${urlCategory}/in/${urlLocation}`;

      return { href };
    },
    [location, category],
  );

  const handleFormSubmit = ({
    categoryId: newCategoryId,
    categoryLabel,
    location: newLocation,
  }: {
    categoryId: number;
    categoryLabel: string;
    location: string;
  }) => {
    track('global.search', {
      location: newLocation,
      category: newCategoryId,
      categoryLabel: categoryLabel,
      resource_type: 'search',
    });
    router.push(
      buildUrls(1, {
        categoryId: newCategoryId,
        location: newLocation,
        county: null,
      }).href,
    );
  };

  const closeCompanyFunnelling = () => {
    setMessageTradeData(null);
  };

  const showRefineCategoriesHandler = () => {
    if (refineCategories.length && !showRefineCategoriesModal) {
      setShowRefineCategoriesModal(true);
    } else {
      setShowRefineCategoriesModal(false);
    }
  };

  const hasFunnel = !!funnelJourney && !!funnelJourney.startingScreen;
  const showSearchFiltering = !(algorithm && category && category.id === 10);

  const meta = [];

  if (noindex) {
    meta.push('noindex');
  } else {
    meta.push('index');
  }

  if (nofollow) {
    meta.push('nofollow');
  } else {
    meta.push('follow');
  }

  const metaRobot = meta.join(', ');

  return (
    <>
      <Head>
        <title>{formatPageTitle(pageTitle)}</title>
        <link rel="canonical" href={canonicalUrl} />
        <meta name="description" content={description} className="test" />
        <meta name="cat-category-id" content={`${category.id}`} />
        <meta name="cat-category-label" content={category.label} />
        {metaRobot && <meta name="robots" content={metaRobot} />}
        <link rel="preconnect" href="https://image-service.checkatrade.net/" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(localBusiness) }}
        />
      </Head>

      <Schema breadcrumbs={breadcrumbs} />
      <DockWrapper>
        <SearchDock
          onSearchSubmit={handleFormSubmit}
          categoryId={category.id}
          location={capitalisedLocation}
          categoryLabel={category.label}
          categories={categories}
          handleCategorySearch={handleCategorySearch}
          journey={funnelJourney}
          hasFunnel={hasFunnel}
          showFilterButton={
            (showSearchFiltering && hasFunnel) || refineCategories?.length > 0
          }
          onShowRefineCategories={showRefineCategoriesHandler}
        />
      </DockWrapper>

      <Wrapper>
        {showError && <SearchError error={error} onTrackEvent={onTrackEvent} />}
        {showInit && <SearchInit />}

        {showNoResults && <SearchNoResults />}

        {showContent && (
          <JobRefinementProvider
            key={form.formId}
            form={form}
            category={category}
            location={location}
            postcode={postcode}
            enableRAQOption={form?.variant === 'variant_c'}
          >
            <SearchContent
              {...props}
              loading={loading}
              heading={heading}
              showAmbiguousLocations={showAmbiguousLocations}
              algorithm={algorithm}
              jobRefinementEnabled={jobRefinementEnabled}
              setMessageTradeData={setMessageTradeData}
              setMoreTradesLoading={setMoreTradesLoading}
              moreTradesLoading={moreTradesLoading}
              setAlgorithm={setAlgorithm}
            />
          </JobRefinementProvider>
        )}

        {(showNoResults || showContent) && (
          <>
            <SearchRedesignBottomStuff>
              {category.label && (
                <TradeSignupBannerRedesign
                  category={category.label}
                  location={formattedLocation}
                />
              )}
              <CategoryBlurbRedesign content={blurbContent} />
              <SearchBreadcrumbsRedesign
                breadcrumbs={breadcrumbs}
                location={location}
              />
            </SearchRedesignBottomStuff>
          </>
        )}
      </Wrapper>

      <Interlinks
        interlinks={seoContent?.interlinks}
        category={category?.name}
        location={location}
      />
      {messageTradeData && (
        <JobContextProvider
          initialState={{
            type: JOB_TYPE.directMessage,
            categoryId: category.id,
            leads: [messageTradeData.companyId],
          }}
        >
          <TradeProvider initialValue={messageTradeData}>
            <RAQFunnel
              from="search"
              jobRefinementEnabled={jobRefinementEnabled}
              name={messageTradeData.name}
              companyId={messageTradeData.companyId}
              categoryId={category.id}
              skills={messageTradeData.skills}
              onDismiss={closeCompanyFunnelling}
              uniqueName={messageTradeData.uniqueName}
              searchLocation={location}
            />
          </TradeProvider>
        </JobContextProvider>
      )}

      {showRefineCategoriesModal && (
        <RefineCategoriesModal
          categories={refineCategories as SingleCategory[]}
          currentCategory={category.id}
          title="Filter your search"
          onClose={showRefineCategoriesHandler}
          getUrl={(vanityCategory?: string) =>
            buildVanityUrl({ vanityCategory })
          }
        />
      )}
      <AppPromotionPopup />
    </>
  );
};

export default SearchPage;
